import { useCallback, useEffect, useMemo, useState } from "react";
import { Building2, KeyRound, Loader2, MapPin, Pencil, Percent, Plus, Power, RotateCcw, Search, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { fetchAdminCustomers } from "@/lib/adminCustomersApi";
import {
  assignDeliveryUnitPriceTable,
  createDeliveryUnit,
  deactivateDeliveryUnit,
  fetchDeliveryUnits,
  reactivateDeliveryUnit,
  updateDeliveryUnit,
  fetchUnitLogins,
  createUnitLogin,
  resetUnitLoginPassword,
  deactivateUnitLogin,
  reactivateUnitLogin,
  backfillUnitLogins,
  type DeliveryUnit,
  type DeliveryUnitAccessMode,
  type DeliveryUnitLogin,
  type DeliveryUnitLoginCreated,
  type UnitLoginBackfillReport,
} from "@/lib/adminDeliveryUnitsApi";
import { fetchPriceTables } from "@/lib/adminPriceTablesApi";
import { getApiErrorMessage } from "@/lib/api";
import { useApp } from "@/store/useApp";
import { hasPermission, type AdminClientSummary, type PriceTable } from "@/lib/types";
import { NegotiationConditionPanel } from "@/components/admin/NegotiationConditionPanel";

/** Valor sentinela do Select: unidade sem tabela própria usa a tabela do cliente. */
const CLIENT_TABLE_VALUE = "__client__";

interface UnitForm {
  externalCode: string;
  name: string;
  address: string;
  district: string;
  city: string;
  postalCode: string;
  deliveryWindowText: string;
  email: string;
  accessMode: DeliveryUnitAccessMode;
}

const EMPTY_FORM: UnitForm = {
  externalCode: "",
  name: "",
  address: "",
  district: "",
  city: "",
  postalCode: "",
  deliveryWindowText: "",
  email: "",
  accessMode: "ACESSO_COMPARTILHADO",
};

function formForUnit(unit: DeliveryUnit): UnitForm {
  return {
    externalCode: unit.externalCode,
    name: unit.name,
    address: unit.address ?? "",
    district: unit.district ?? "",
    city: unit.city ?? "",
    postalCode: unit.postalCode ?? "",
    deliveryWindowText: unit.deliveryWindowText ?? "",
    email: unit.email ?? "",
    accessMode: unit.accessMode ?? "ACESSO_COMPARTILHADO",
  };
}

function getClientLabel(client: AdminClientSummary) {
  return client.companyName?.trim() || client.email;
}

export default function AdminDeliveryUnits() {
  const { account } = useApp();
  // Ações por permissão separada (unidade e acesso de unidade). Route já exige CLIENTES.UNIDADE.VISUALIZAR.
  const canViewAccess = hasPermission(account, "CLIENTES.UNIDADE.VISUALIZAR");
  const canCreateUnit = hasPermission(account, "CLIENTES.UNIDADE.CRIAR");
  const canEditUnit = hasPermission(account, "CLIENTES.UNIDADE.EDITAR");
  const canDeactivateUnit = hasPermission(account, "CLIENTES.UNIDADE.DESATIVAR");
  const canReactivateUnit = hasPermission(account, "CLIENTES.UNIDADE.REATIVAR");
  const canAssignTable = hasPermission(account, "CLIENTES.UNIDADE.TABELA_PRECO");
  const canCreateAccess = hasPermission(account, "CLIENTES.UNIDADE.ACESSO_CRIAR");
  const canResetAccess = hasPermission(account, "CLIENTES.UNIDADE.ACESSO_RESETAR_SENHA");
  const canDeactivateAccess = hasPermission(account, "CLIENTES.UNIDADE.ACESSO_DESATIVAR");
  const canReactivateAccess = hasPermission(account, "CLIENTES.UNIDADE.ACESSO_REATIVAR");
  const canViewNegotiation = hasPermission(account, "CLIENTES.NEGOCIACAO.VISUALIZAR");
  const canEditNegotiation = hasPermission(account, "CLIENTES.NEGOCIACAO.EDITAR");
  const canEdit = canViewAccess || canViewNegotiation; // coluna de ações visível a quem pode ver a unidade/negociação

  const [clients, setClients] = useState<AdminClientSummary[]>([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedClientId, setSelectedClientId] = useState<string>("");
  const [units, setUnits] = useState<DeliveryUnit[]>([]);
  const [unitsLoading, setUnitsLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<DeliveryUnit | null>(null);
  const [form, setForm] = useState<UnitForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState<DeliveryUnit | null>(null);
  const [priceTables, setPriceTables] = useState<PriceTable[]>([]);
  const [assigningUnitId, setAssigningUnitId] = useState<string | null>(null);

  // Gestão de logins (acessos) da unidade.
  const [accessUnit, setAccessUnit] = useState<DeliveryUnit | null>(null);
  const [negotiationUnit, setNegotiationUnit] = useState<DeliveryUnit | null>(null);
  const [logins, setLogins] = useState<DeliveryUnitLogin[]>([]);
  const [loginsLoading, setLoginsLoading] = useState(false);
  const [newLoginEmail, setNewLoginEmail] = useState("");
  const [creatingLogin, setCreatingLogin] = useState(false);
  const [credential, setCredential] = useState<DeliveryUnitLoginCreated | null>(null);

  // Backfill (conversão do titular para logins por unidade).
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [backfillReport, setBackfillReport] = useState<UnitLoginBackfillReport | null>(null);
  const [backfillRunning, setBackfillRunning] = useState(false);

  useEffect(() => {
    // Seletor de cliente é auxiliar: só busca para quem pode ver clientes (evita 403 no escopo de
    // quem só administra unidades).
    if (!hasPermission(useApp.getState().account, "CLIENTES.CLIENTE.VISUALIZAR")) return;
    setClientsLoading(true);
    fetchAdminCustomers()
      .then((list) => {
        const validated = list.filter((c) => c.status === "VALIDADO");
        setClients(validated);
        if (!selectedClientId && validated.length > 0) {
          setSelectedClientId(validated[0].id);
        }
      })
      .catch((err) => toast.error(getApiErrorMessage(err, "Erro ao carregar clientes")))
      .finally(() => setClientsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUnits = useCallback(async (clientId: string) => {
    if (!clientId) {
      setUnits([]);
      return;
    }
    setUnitsLoading(true);
    try {
      setUnits(await fetchDeliveryUnits(clientId));
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Erro ao carregar unidades"));
      setUnits([]);
    } finally {
      setUnitsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadUnits(selectedClientId);
  }, [selectedClientId, loadUnits]);

  const filteredClients = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return clients;
    return clients.filter((c) =>
      (c.companyName ?? "").toLowerCase().includes(term) || c.email.toLowerCase().includes(term),
    );
  }, [clients, search]);

  const selectedClient = clients.find((c) => c.id === selectedClientId);

  useEffect(() => {
    // Tabelas de preço são auxiliares (atribuição por unidade): só busca com permissão de vê-las.
    if (!hasPermission(useApp.getState().account, "PRECOS.TABELA.VISUALIZAR")) return;
    fetchPriceTables()
      .then(setPriceTables)
      .catch((err) => toast.error(getApiErrorMessage(err, "Erro ao carregar tabelas de preço")));
  }, []);

  // Só tabelas ativas do MESMO segmento do cliente podem ser atribuídas (o backend também valida).
  const segmentTables = useMemo(() => {
    const segment = selectedClient?.clientType;
    if (!segment) return [] as PriceTable[];
    return priceTables.filter((t) => t.active && t.scope === segment);
  }, [priceTables, selectedClient?.clientType]);

  const handleAssignPriceTable = async (unit: DeliveryUnit, value: string) => {
    if (!selectedClientId) return;
    const priceTableId = value === CLIENT_TABLE_VALUE ? null : value;
    setAssigningUnitId(unit.id);
    try {
      const updated = await assignDeliveryUnitPriceTable(selectedClientId, unit.id, priceTableId);
      setUnits((current) => current.map((u) => (u.id === updated.id ? updated : u)));
      toast.success(priceTableId ? "Tabela da unidade atualizada" : "Unidade voltou a usar a tabela do cliente");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Erro ao atribuir tabela"));
    } finally {
      setAssigningUnitId(null);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (unit: DeliveryUnit) => {
    setEditing(unit);
    setForm(formForUnit(unit));
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const handleSave = async () => {
    if (!selectedClientId) return;
    if (!form.externalCode.trim()) {
      toast.error("Código é obrigatório");
      return;
    }
    if (!form.name.trim()) {
      toast.error("Nome é obrigatório");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        externalCode: form.externalCode.trim(),
        name: form.name.trim(),
        address: form.address.trim() || null,
        district: form.district.trim() || null,
        city: form.city.trim() || null,
        postalCode: form.postalCode.trim() || null,
        deliveryWindowText: form.deliveryWindowText.trim() || null,
        email: form.email.trim() || null,
        accessMode: form.accessMode,
      };
      if (editing) {
        const updated = await updateDeliveryUnit(selectedClientId, editing.id, payload);
        setUnits((current) => current.map((u) => (u.id === updated.id ? updated : u)));
        toast.success("Unidade atualizada");
      } else {
        const created = await createDeliveryUnit(selectedClientId, payload);
        setUnits((current) => [...current, created].sort((a, b) => a.name.localeCompare(b.name)));
        toast.success("Unidade criada");
      }
      closeDialog();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Erro ao salvar unidade"));
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget || !selectedClientId) return;
    try {
      const updated = await deactivateDeliveryUnit(selectedClientId, deactivateTarget.id);
      setUnits((current) => current.map((u) => (u.id === updated.id ? updated : u)));
      toast.success("Unidade desativada");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Erro ao desativar"));
    } finally {
      setDeactivateTarget(null);
    }
  };

  const handleReactivate = async (unit: DeliveryUnit) => {
    if (!selectedClientId) return;
    try {
      const updated = await reactivateDeliveryUnit(selectedClientId, unit.id);
      setUnits((current) => current.map((u) => (u.id === updated.id ? updated : u)));
      toast.success("Unidade reativada");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Erro ao reativar"));
    }
  };

  const openAccess = async (unit: DeliveryUnit) => {
    if (!selectedClientId) return;
    setAccessUnit(unit);
    setCredential(null);
    setNewLoginEmail(unit.email ?? "");
    setLoginsLoading(true);
    try {
      setLogins(await fetchUnitLogins(selectedClientId, unit.id));
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Erro ao carregar acessos"));
      setLogins([]);
    } finally {
      setLoginsLoading(false);
    }
  };

  const handleCreateLogin = async () => {
    if (!selectedClientId || !accessUnit) return;
    if (!newLoginEmail.trim()) {
      toast.error("Informe o e-mail do acesso");
      return;
    }
    setCreatingLogin(true);
    try {
      const created = await createUnitLogin(selectedClientId, accessUnit.id, newLoginEmail.trim());
      setCredential(created);
      setNewLoginEmail("");
      setLogins(await fetchUnitLogins(selectedClientId, accessUnit.id));
      toast.success("Acesso criado — copie a senha temporária");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Erro ao criar acesso"));
    } finally {
      setCreatingLogin(false);
    }
  };

  const handleResetLogin = async (login: DeliveryUnitLogin) => {
    if (!selectedClientId || !accessUnit) return;
    try {
      setCredential(await resetUnitLoginPassword(selectedClientId, accessUnit.id, login.id));
      setLogins(await fetchUnitLogins(selectedClientId, accessUnit.id));
      toast.success("Senha redefinida — copie a nova senha temporária");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Erro ao redefinir senha"));
    }
  };

  const handleToggleLogin = async (login: DeliveryUnitLogin) => {
    if (!selectedClientId || !accessUnit) return;
    try {
      const updated = login.active
        ? await deactivateUnitLogin(selectedClientId, accessUnit.id, login.id)
        : await reactivateUnitLogin(selectedClientId, accessUnit.id, login.id);
      setLogins((current) => current.map((l) => (l.id === updated.id ? updated : l)));
      toast.success(updated.active ? "Acesso reativado" : "Acesso desativado");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Erro ao alterar acesso"));
    }
  };

  const runBackfill = async (execute: boolean) => {
    if (!selectedClientId) return;
    setBackfillRunning(true);
    try {
      const report = await backfillUnitLogins(selectedClientId, !execute);
      setBackfillReport(report);
      if (execute) {
        toast.success(report.titularConverted ? "Titular convertido em logins por unidade" : "Backfill executado");
        void loadUnits(selectedClientId);
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Erro no backfill"));
    } finally {
      setBackfillRunning(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="rounded-2xl bg-gradient-to-br from-primary to-primary/80 p-6 text-primary-foreground">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Unidades de entrega</h1>
            <p className="mt-1 text-sm text-primary-foreground/80">
              Gerencie as unidades de entrega de cada cliente, com endereço, janela e email da unidade.
            </p>
          </div>
          {(canCreateAccess || canCreateUnit) && selectedClientId && (
            <div className="flex gap-2">
              {canCreateAccess && (
                <Button
                  variant="secondary"
                  className="gap-2"
                  onClick={() => { setBackfillReport(null); setBackfillOpen(true); }}
                  title="Criar logins por unidade e converter o titular em conta não-operacional"
                >
                  <Users className="h-4 w-4" />
                  Converter em logins por unidade
                </Button>
              )}
              {canCreateUnit && (
                <Button variant="secondary" className="gap-2" onClick={openCreate}>
                  <Plus className="h-4 w-4" />
                  Nova unidade
                </Button>
              )}
            </div>
          )}
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-[2fr_3fr]">
          <div className="flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2">
            <Search className="h-4 w-4 text-primary-foreground/60" />
            <input
              className="flex-1 bg-transparent text-sm text-primary-foreground placeholder:text-primary-foreground/50 outline-none"
              placeholder="Buscar cliente"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Select value={selectedClientId} onValueChange={setSelectedClientId} disabled={clientsLoading}>
            <SelectTrigger className="bg-white/10 text-primary-foreground">
              <SelectValue placeholder={clientsLoading ? "Carregando..." : "Selecione um cliente"} />
            </SelectTrigger>
            <SelectContent>
              {filteredClients.map((client) => (
                <SelectItem key={client.id} value={client.id}>
                  {getClientLabel(client)}
                </SelectItem>
              ))}
              {filteredClients.length === 0 && (
                <div className="px-3 py-2 text-xs text-muted-foreground">Nenhum cliente encontrado</div>
              )}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!selectedClientId ? (
        <div className="flex flex-col items-center gap-3 py-16 text-muted-foreground">
          <Building2 className="h-10 w-10 opacity-30" />
          <p className="text-sm">Selecione um cliente para visualizar as unidades.</p>
        </div>
      ) : unitsLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : units.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-16 text-muted-foreground">
          <Building2 className="h-10 w-10 opacity-30" />
          <p className="text-sm">
            {selectedClient ? `${getClientLabel(selectedClient)} ainda não tem unidades cadastradas.` : "Sem unidades."}
          </p>
          {canCreateUnit && (
            <Button variant="outline" className="gap-2" onClick={openCreate}>
              <Plus className="h-4 w-4" />
              Cadastrar primeira unidade
            </Button>
          )}
        </div>
      ) : (
        <div className="rounded-xl border bg-card shadow-sm">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Código</TableHead>
                <TableHead>Nome</TableHead>
                <TableHead>Endereço</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Janela</TableHead>
                <TableHead>Tabela de preços</TableHead>
                {canEdit && <TableHead className="text-right">Ações</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {units.map((unit) => (
                <TableRow key={unit.id} className={unit.active ? "" : "opacity-60"}>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <Badge variant="outline" className="font-mono text-xs">
                        {unit.externalCode}
                      </Badge>
                      {!unit.active && (
                        <Badge variant="outline" className="border-amber-300 bg-amber-50 text-[10px] font-normal text-amber-700">
                          Inativa
                        </Badge>
                      )}
                      {unit.accessMode === "ACESSO_INDEPENDENTE" && (
                        <Badge variant="outline" className="border-sky-300 bg-sky-50 text-[10px] font-normal text-sky-700">
                          Independente
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">{unit.name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {unit.address ? (
                      <div className="flex items-start gap-1.5">
                        <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 opacity-70" />
                        <div>
                          <div>{unit.address}</div>
                          {(unit.district || unit.city) && (
                            <div className="text-xs">
                              {[unit.district, unit.city].filter(Boolean).join(" · ")}
                              {unit.postalCode ? ` · ${unit.postalCode}` : ""}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">{unit.email ?? <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {unit.deliveryWindowText ?? "—"}
                  </TableCell>
                  <TableCell className="text-sm">
                    {canAssignTable ? (
                      <Select
                        value={unit.priceTableId ?? CLIENT_TABLE_VALUE}
                        onValueChange={(value) => void handleAssignPriceTable(unit, value)}
                        disabled={assigningUnitId === unit.id || !selectedClient?.clientType}
                      >
                        <SelectTrigger className="h-8 w-[220px] text-xs">
                          <SelectValue placeholder="Tabela do cliente" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={CLIENT_TABLE_VALUE}>Tabela do cliente (padrão)</SelectItem>
                          {segmentTables.map((table) => (
                            <SelectItem key={table.id} value={table.id}>
                              {table.name}
                            </SelectItem>
                          ))}
                          {segmentTables.length === 0 && (
                            <div className="px-3 py-2 text-xs text-muted-foreground">
                              Nenhuma tabela do segmento do cliente
                            </div>
                          )}
                        </SelectContent>
                      </Select>
                    ) : unit.priceTableId ? (
                      priceTables.find((t) => t.id === unit.priceTableId)?.name ?? "Tabela própria"
                    ) : (
                      <span className="text-muted-foreground">Tabela do cliente</span>
                    )}
                  </TableCell>
                  {canEdit && (
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {canEditUnit && (
                          <Button variant="ghost" size="icon" onClick={() => openEdit(unit)} title="Editar">
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        {canViewAccess && (
                          <Button variant="ghost" size="icon" onClick={() => void openAccess(unit)} title="Acessos da unidade">
                            <KeyRound className="h-4 w-4" />
                          </Button>
                        )}
                        {canViewNegotiation && (
                          <Button variant="ghost" size="icon" onClick={() => setNegotiationUnit(unit)} title="Condições da negociação">
                            <Percent className="h-4 w-4" />
                          </Button>
                        )}
                        {unit.active ? (
                          canDeactivateUnit && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setDeactivateTarget(unit)}
                              title="Desativar"
                            >
                              <Power className="h-4 w-4 text-destructive" />
                            </Button>
                          )
                        ) : (
                          canReactivateUnit && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => void handleReactivate(unit)}
                              title="Reativar"
                            >
                              <RotateCcw className="h-4 w-4 text-emerald-600" />
                            </Button>
                          )
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={negotiationUnit !== null} onOpenChange={(open) => { if (!open) setNegotiationUnit(null); }}>
        <DialogContent className="flex max-h-[90vh] max-w-3xl flex-col overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Condições da negociação — {negotiationUnit?.name}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            Exceção da unidade. Campos vazios herdam do cliente; a condição efetiva (com origem) é exibida abaixo.
          </p>
          {negotiationUnit && selectedClientId && (
            <NegotiationConditionPanel
              customerId={selectedClientId}
              unitId={negotiationUnit.id}
              canEdit={canEditNegotiation}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) closeDialog(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar unidade" : "Nova unidade de entrega"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Código *</Label>
              <Input
                value={form.externalCode}
                onChange={(e) => setForm({ ...form, externalCode: e.target.value })}
                placeholder="Ex.: UN-001"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Nome *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ex.: Matriz Centro"
              />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label>Endereço</Label>
              <Input
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Bairro</Label>
              <Input
                value={form.district}
                onChange={(e) => setForm({ ...form, district: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Cidade</Label>
              <Input
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>CEP</Label>
              <Input
                value={form.postalCode}
                onChange={(e) => setForm({ ...form, postalCode: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Janela de entrega</Label>
              <Input
                value={form.deliveryWindowText}
                onChange={(e) => setForm({ ...form, deliveryWindowText: e.target.value })}
                placeholder="Ex.: 08h às 12h"
              />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label>Email da unidade</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Ex.: compras@unidade.com.br"
              />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label>Modo de acesso</Label>
              <Select
                value={form.accessMode}
                onValueChange={(value) => setForm({ ...form, accessMode: value as DeliveryUnitAccessMode })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ACESSO_COMPARTILHADO">Compartilhado (titular vê e troca de unidade)</SelectItem>
                  <SelectItem value="ACESSO_INDEPENDENTE">Independente (unidade isolada: só vê a própria)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Independente isola preço, carrinho e pedidos da unidade. A gestão das contas de usuário da
                unidade é feita separadamente.
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={closeDialog} disabled={saving}>Cancelar</Button>
            <Button onClick={() => void handleSave()} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editing ? "Salvar" : "Criar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deactivateTarget !== null} onOpenChange={(open) => { if (!open) setDeactivateTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Desativar unidade</DialogTitle>
          </DialogHeader>
          {deactivateTarget && (
            <p className="text-sm text-muted-foreground">
              A unidade <strong>{deactivateTarget.name}</strong> ({deactivateTarget.externalCode}) será desativada
              e não aparecerá em novos pedidos. Você pode reativá-la a qualquer momento.
            </p>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeactivateTarget(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={() => void handleDeactivate()}>
              Desativar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Gestão de acessos (logins) da unidade */}
      <Dialog open={accessUnit !== null} onOpenChange={(open) => { if (!open) { setAccessUnit(null); setCredential(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Acessos — {accessUnit?.name}</DialogTitle>
            <DialogDescription>
              Logins exclusivos desta unidade. Cada acesso enxerga apenas esta unidade (preço, carrinho e
              pedidos isolados). A senha temporária aparece uma única vez.
            </DialogDescription>
          </DialogHeader>

          {credential && (
            <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm">
              <p className="font-medium text-emerald-800">Credenciais criadas — comunique ao cliente:</p>
              <p className="mt-1">E-mail: <span className="font-mono">{credential.email}</span></p>
              <p>Senha temporária: <span className="font-mono font-semibold">{credential.temporaryPassword}</span></p>
              <p className="mt-1 text-xs text-emerald-700">O cliente deverá trocar a senha no primeiro login.</p>
            </div>
          )}

          {canCreateAccess && (
            <div className="flex items-end gap-2">
              <div className="flex-1 space-y-1.5">
                <Label>E-mail do novo acesso</Label>
                <Input
                  type="email"
                  value={newLoginEmail}
                  onChange={(e) => setNewLoginEmail(e.target.value)}
                  placeholder="Ex.: compras.unidade@cliente.com.br"
                />
              </div>
              <Button onClick={() => void handleCreateLogin()} disabled={creatingLogin}>
                {creatingLogin && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Criar acesso
              </Button>
            </div>
          )}

          <div className="mt-2 flex flex-col gap-2">
            {loginsLoading ? (
              <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
            ) : logins.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">Nenhum acesso cadastrado para esta unidade.</p>
            ) : (
              logins.map((login) => (
                <div key={login.id} className="flex items-center justify-between rounded-lg border p-2.5">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{login.email}</p>
                    <div className="flex gap-1.5 pt-0.5">
                      <Badge variant="outline" className={login.active ? "text-[10px] text-emerald-700 border-emerald-300 bg-emerald-50" : "text-[10px] text-amber-700 border-amber-300 bg-amber-50"}>
                        {login.active ? "Ativo" : "Inativo"}
                      </Badge>
                      {login.passwordResetRequired && (
                        <Badge variant="outline" className="text-[10px] text-sky-700 border-sky-300 bg-sky-50">Reset pendente</Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    {canResetAccess && (
                      <Button variant="ghost" size="sm" onClick={() => void handleResetLogin(login)}>Resetar senha</Button>
                    )}
                    {(login.active ? canDeactivateAccess : canReactivateAccess) && (
                      <Button variant="ghost" size="sm" onClick={() => void handleToggleLogin(login)}>
                        {login.active ? "Desativar" : "Reativar"}
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Backfill: conversão do titular em logins por unidade */}
      <Dialog open={backfillOpen} onOpenChange={(open) => { if (!open) { setBackfillOpen(false); setBackfillReport(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Converter em logins por unidade</DialogTitle>
            <DialogDescription>
              Cria um login para cada unidade ativa sem acesso e, quando todas estiverem cobertas, converte o
              titular em conta comercial read-only (não-operacional). Rode a simulação antes de executar.
            </DialogDescription>
          </DialogHeader>

          {backfillReport && (
            <div className="max-h-72 overflow-auto rounded-lg border p-3 text-sm">
              <p className="mb-2 font-medium">
                {backfillReport.dryRun ? "Simulação" : "Execução"} ·{" "}
                {backfillReport.dryRun
                  ? (backfillReport.titularWouldConvert ? "titular seria convertido" : "titular NÃO seria convertido")
                  : (backfillReport.titularConverted ? "titular convertido" : "titular não convertido")}
              </p>
              <ul className="flex flex-col gap-1.5">
                {backfillReport.units.map((u) => (
                  <li key={u.unitId} className="flex flex-col">
                    <span className="font-medium">{u.unitName} <span className="font-normal text-muted-foreground">— {u.action}</span></span>
                    {u.email && <span className="text-xs">{u.email}</span>}
                    {u.temporaryPassword && (
                      <span className="text-xs">senha: <span className="font-mono">{u.temporaryPassword}</span></span>
                    )}
                  </li>
                ))}
              </ul>
              {backfillReport.warnings.length > 0 && (
                <div className="mt-2 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800">
                  {backfillReport.warnings.map((w, i) => <p key={i}>{w}</p>)}
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => void runBackfill(false)} disabled={backfillRunning}>
              {backfillRunning && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Simular (dry-run)
            </Button>
            <Button onClick={() => void runBackfill(true)} disabled={backfillRunning}>
              Executar conversão
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
